import React, { Component, useEffect, useState } from 'react'
import { Form, FormInstance, Skeleton, Alert } from 'antd'
import WrapperMappingData from './WrapperMappingData'
import { SearchOptions } from '../../TableHelper'

// Forms — each mapping type renders its own self-contained stepped form.
import MappingAgeForm from './Age'
import MappingAgeRangeForm from './AgeRange'
import MappingBreedForm from './Breed'
import MappingSexForm from './Sex'
import MappingStateForm from './State'
import MappingSizeForm from './Size'
import MappingSpecieForm from './Specie'
import MappingCharacteristicForm from './Characteristic'
import MappingColorForm from './Color'
import MappingBranchForm from './Branch'

type MappingProps = {
  loadingContent: boolean,

  activeTab: string,
  uuid?: string | null,

  searchOptions: SearchOptions | null,

  partners: any | null,
  loadPartners: Function

  onSave?: () => void | Promise<void>
}

/** Context data provided by `WrapperMappingData` for the active mapping type. */
type MappingDataContext = {
  values: any,
  loading: boolean,
  listMappings: (options: any) => Promise<any>,
  loadMapping: (options: any) => Promise<any>,
  saveMapping: (payload: any) => Promise<any>,
}

type RenderFormExtra = {
  loading?: boolean
}

type MappingFormProps = MappingProps & MappingDataContext & {
  renderForm: (activeTab: string, form: FormInstance, extra?: RenderFormExtra) => React.ReactNode,
}

/**
 * Loads the edited entity whenever the form mounts for an edit (the modal is
 * `destroyOnClose`, so this remounts on every open). It always re-fetches rather
 * than trusting whatever the context still holds — otherwise reopening the same
 * record would show stale data, since the context value survives the modal close.
 * A skeleton is shown until the fetch settles, then the form mounts with fresh data.
 */
function MappingFormLoader(props: MappingFormProps) {
  const { uuid, loadMapping } = props

  // "add" mode (no uuid) is ready immediately; an edit waits for its fetch.
  const [ready, setReady] = useState(!uuid)

  useEffect(() => {
    if (!uuid) {
      setReady(true)
      return
    }

    let active = true
    setReady(false)

    Promise.resolve(
      loadMapping({
        search: { uuid },
        options: { _force_ids: true }
      })
    )
      .catch(() => {
        // Load errors surface through the context error state; still let the
        // form mount so we don't get stuck on an endless skeleton.
      })
      .finally(() => {
        if (active) setReady(true)
      })

    return () => { active = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid])

  if (!ready) {
    return <Skeleton />
  }

  return <MappingEntityForm {...props} />
}

/** The actual mapping form. Mounted only once the entity data is ready. */
function MappingEntityForm(props: MappingFormProps) {
  const {
    activeTab, uuid, searchOptions,
    values, loading, listMappings, saveMapping, renderForm,
  } = props

  const [form] = Form.useForm()
  const [savingFeedback, _savingFeedback] = useState<string | null>(null)
  let feedbackTimer: any = null
  const setSavingFeedback = (message: string | null, timeout?: number) => {
    if (feedbackTimer) {
      clearTimeout(feedbackTimer)
      feedbackTimer = null
    }

    _savingFeedback(message)

    if (timeout) {
      feedbackTimer = setTimeout(() => setSavingFeedback(null), 3000)
    }
  }

  const handleSubmit = (vals: any) => {
    // Parse external id (may be absent, e.g. a MATCH/INPUT color mapping)
    const payload = {
      uuid,
      ...vals,
      external_id: vals.external_id != null ? vals.external_id.toString() : vals.external_id,
      external_country_id: vals.external_country_id != null ? vals.external_country_id.toString() : vals.external_country_id,
      // An untouched switch leaves `active` undefined; the API treats undefined as
      // true, so coerce it to a real boolean (a new mapping defaults to inactive).
      // Only when the form actually has an active field (age-range has none).
      ...('active' in vals ? { active: !!vals.active } : {}),
    }

    // DIRECT mappings have a default direction (BOTH) — don't send `direction_key` at all.
    if (payload.type_key === 'DIRECT') {
      delete payload.direction_key
    }

    setSavingFeedback(null)

    // Refresh the underlying list once the save succeeds, so it doesn't stay stale.
    Promise.resolve(saveMapping(payload))
      .then(() => {
        setSavingFeedback("success", 3000)

        return Promise.allSettled([
          props.onSave?.(),
          searchOptions && listMappings(searchOptions)
        ])
      })
      .catch((e) => {
        // Save errors are surfaced through the form/context error state.
        setSavingFeedback(e?.message)
      })
  }

  return (
    <Form
      form={form}
      name={`mapping_${activeTab}`}
      layout="vertical"
      initialValues={uuid ? values : null}
      preserve={false}
      onFinish={handleSubmit}
    >
      {
        savingFeedback ? (
          <Alert
            message={savingFeedback === "success" ? "Saved" : "Failed"}
            description={savingFeedback === "success" ? "Mapping saved successfully!" : savingFeedback}
            type={savingFeedback === "success" ? "success" : "error"}
            style={{ marginBottom: 15 }}
            showIcon
          />
        ) : null
      }

      {/* Each form is self-contained: it renders its own fields and Save button. */}
      {renderForm(activeTab, form, { loading })}
    </Form>
  )
}

export default class Mapping extends Component<MappingProps> {

  static defaultProps = {
    loadingContent: false,
    uuid: null
  }

  state = {
    loaded: false
  }

  static getDerivedStateFromProps(props: any, state: any) {
    // Keep our partners loaded
    if (!props.loadingContent && !props.partners) {
      state.loaded = false

      props.loadPartners({
        options: {
          limit: 9999
        }
      })
    }

    if (!props.loadingContent && props.partners && !state.loaded) {
      state.loaded = true
    }

    return state
  }

  renderForm = (activeTab: string, form: FormInstance, extra?: RenderFormExtra) => {
    switch (activeTab) {
      case 'age':
        return <MappingAgeForm form={form} partnerList={this.props.partners} loading={extra?.loading} />
      case 'age-range':
        return <MappingAgeRangeForm form={form} loading={extra?.loading} />
      case 'breed':
        return <MappingBreedForm form={form} partnerList={this.props.partners} loading={extra?.loading} />
      case 'sex':
        return <MappingSexForm form={form} partnerList={this.props.partners} loading={extra?.loading} />
      case 'state':
        return <MappingStateForm form={form} partnerList={this.props.partners} loading={extra?.loading} />
      case 'size':
        return <MappingSizeForm form={form} partnerList={this.props.partners} loading={extra?.loading} />
      case 'specie':
        return <MappingSpecieForm form={form} partnerList={this.props.partners} loading={extra?.loading} />
      case 'color':
        return <MappingColorForm form={form} partnerList={this.props.partners} loading={extra?.loading} />
      case 'branch':
        return <MappingBranchForm form={form} partnerList={this.props.partners} loading={extra?.loading} />
      case 'characteristic':
        return <MappingCharacteristicForm form={form} partnerList={this.props.partners} loading={extra?.loading} />
    }
  }

  render() {
    const { activeTab } = this.props

    return (
      <WrapperMappingData activeTab={activeTab}>
        {({ values, loading, listMappings, loadMapping, saveMapping }: any) => (
          <MappingFormLoader
            {...this.props}
            values={values}
            loading={loading}
            listMappings={listMappings}
            loadMapping={loadMapping}
            saveMapping={saveMapping}
            renderForm={this.renderForm}
          />
        )}
      </WrapperMappingData>
    )
  }
}
